import { InfoCircle } from '@tencent/odc-iconpack';
import 'highlight.js/styles/github.css';
import { Skeleton, message } from 'odc-shared/components';
import React, { useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

import { getSupportContents } from '@src/api/generated/Support';

import MdNavbar from '../mdNavbar';

import CopyButton from './widgets/CopyButton';
import ImageZoom from './widgets/ImageZoom';

import './style.scss';

interface ContentProps {
  pid: string,
  title: string,
}

const getTextContent = (n: any): string => {
  if (!n) return '';
  if (n.type === 'text') return n.value || '';
  if (n.children) return n.children.map(getTextContent).join('');
  return '';
};

const getClassName = (n: any): string => {
  const content = getTextContent(n);
  if (content.length > 600) {
    return 'min-w-400!';
  }
  if (content.length > 400) {
    return 'min-w-300!';
  }
  return '';
};

const EMAIL_REGEX = /^[\w-]+(\.[\w-]+)*@[\w-]+(\.[\w-]+)+$/;

const loadingContent = <Skeleton rows={['100%', '100%', '91%', '79%', '56%', '26%'].map(width => ({ width }))} />;
function Content({
  pid,
  title,
}: ContentProps) {
  const [source, setSource] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState<boolean>(true);
  useEffect(() => {
    const getDocumentSource = async (pid: string) => {
      setLoading(true);
      try {
        const { code, data } = await getSupportContents({
          post_id: pid,
        });
        if (code === 0) {
          setSource(data.post?.content || '');
        }
        containerRef.current?.scrollTo({ top: 0 });
      } catch (error) {
        console.error('Error fetching markdown file:', error);
        message.error('Something went wrong. Please try again later.');
        setSource('');
      }
      setLoading(false);
    };
    if (pid) {
      getDocumentSource(pid);
    }
  }, [containerRef, pid]);
  return (
    <div className="md-page-container" ref={containerRef}>
      <div className="md-page">
        <div className="md-content prose prose-slate prose-a:text-primary">
          {/* <p className="md-path">
            {props.path?.map((pathItem, index) => <span key={pathItem}>
              {index !== 0 && <span className="md-path-divider">/</span>}
              <span key={pathItem} className="md-path-part">{pathItem}</span>
            </span>)}
          </p> */}
          <p className="article-title">{title}</p>
          {
            loading
              ? loadingContent
              : <Markdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  table: ({ children }) => <div className="md-table-container">
                    <table>
                      {children}
                    </table>
                  </div>,
                  td: ({ children, node, ...rest }) => (
                    <td className={getClassName(node)} {...rest}>
                      {children}
                    </td>
                  ),
                  a: ({ href, children }) => {
                    // 判断是否为邮箱链接
                    const isEmail = href?.startsWith('mailto:') || EMAIL_REGEX.test(String(children));
                    if (isEmail) {
                      // 邮箱展示文本
                      return <span>{children}</span>;
                    }
                    return <a href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >{children}</a>;
                  },
                  code(props) {
                    const { className, children, node, ...rest } = props;
                    const isCrossLine = node?.position?.start?.line !== node?.position?.end?.line;
                    const match = /language-(\w+)/.exec(className || '');
                    const language = match?.[1] || 'shell';
                    const codeString = String(children).replace(/\n$/, '');

                    return isCrossLine ? (
                      <div className="odc-code-block">
                        <div className="code-block-header">
                          <span className="code-language">{language}</span>
                          <CopyButton code={codeString} />
                        </div>
                        <SyntaxHighlighter
                          style={oneLight}
                          language={language}
                          PreTag="div"
                          showLineNumbers
                          wrapLines={true}
                          wrapLongLines={true}
                          customStyle={{
                            margin: 0,
                            borderRadius: '0 0 8px 8px',
                          }}
                        >
                          {codeString}
                        </SyntaxHighlighter>
                      </div>
                    ) : (
                      <code className={className} {...rest}>
                        {children}
                      </code>
                    );
                  },
                  pre(props) {
                    const { children, ...rest } = props;
                    return (
                      <pre className="not-prose" {...rest}>
                        {children}
                      </pre>
                    );
                  },
                  img(props) {
                    const { src, alt, ...rest } = props;
                    return src ? (
                      <ImageZoom
                        src={src}
                        alt={alt}
                        {...rest}
                      />
                    ) : (
                      <img {...props} />
                    );
                  },
                  blockquote(props) {
                    const { children, ...rest } = props;
                    return (
                      <blockquote className="odc-blockquote" {...rest}>
                        <InfoCircle />
                        {children}
                      </blockquote>
                    );
                  },
                }}
              >
                {source}
              </Markdown>}
        </div>
        {source && <div className="md-nav-container">
          <div className="md-nav">
            <MdNavbar source={source} levelRange={[1, 2]} />
          </div>
        </div>}
      </div>

    </div>
  );
}

export default React.memo(Content);